import { useState, useEffect } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Dialog } from '@components/dialog';
import { useForm } from 'react-hook-form';
import { Form } from '@components/forms/Form';
import { FormSection } from '@components/forms/FormSection';
import { FormFieldType, TFormField } from '@models/forms/formFields.type';
import { useUpdateTask } from '@services/underwritingTasks/useUpdateTask';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { LongDateTimeFormat } from '@utils/date';
import { getDateFormatter } from '@utils/date';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { IssueType, newIssueSelectValues } from '@pages/QuoteDetails/sidebarItems/underwriting/Underwriting.constants';
import { IssueStatus } from '@models/underwritingIssue';

interface EditUWIssueDialogProps {
  open: boolean;
  onClose: () => void;
  selectedIssue: IssueType | null;
}

export const EditUWIssueDialog = ({ open, onClose, selectedIssue }: EditUWIssueDialogProps) => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const { updateTask, isPending: isUpdating } = useUpdateTask(quoteId, selectedIssue?.id || '');
  const [updateTaskError, setUpdateTaskError] = useState<string | null>(null);
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const getEditIssueFields = (statusOptions: { label: string; value: string }[]): { [key: string]: TFormField } => ({
    status: {
      name: 'status',
      fieldType: FormFieldType.SelectField,
      label: 'Status',
      required: true,
      gridWidth: { xs: 12 },
      fieldConfig: {
        selectField: {
          options: statusOptions,
        },
      },
    },
    reasoning: {
      name: 'reasoning',
      fieldType: FormFieldType.TextField,
      label: "Underwriter's Reasoning",
      required: true,
      gridWidth: { xs: 12 },
      fieldConfig: {
        textField: {
          multiline: true,
          rows: 3,
        },
      },
      placeholder: 'Use this space to explain why this issue should or should not be overridden',
    },
  });

  const editIssueForm = useForm({
    mode: 'onChange',
    defaultValues: { status: '', reasoning: '' },
  });

  const editIssueFields = getEditIssueFields(newIssueSelectValues);
  const isEditIssueFormValid = editIssueForm.formState.isValid;

  useEffect(() => {
    if (selectedIssue && open) {
      editIssueForm.reset({
        status: selectedIssue.status || '',
        reasoning: selectedIssue.reasoning || '',
      });
      setUpdateTaskError(null);
    }
  }, [selectedIssue, open, editIssueForm]);

  const handleEditIssueFormSubmit = async (data: { status: string; reasoning: string }) => {
    if (!quoteId || !selectedIssue?.id) return;
    setUpdateTaskError(null);

    try {
      await updateTask({ status: data.status as IssueStatus, underwriterComment: data.reasoning });
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update issue';
      setUpdateTaskError(errorMessage);
    }
  };

  if (!selectedIssue) return null;

  return (
    <Dialog
      open={open}
      handleCloseDialog={onClose}
      title='Edit Underwriting Issue'
      primaryBtnText='Save'
      primaryBtnAction={editIssueForm.handleSubmit(handleEditIssueFormSubmit)}
      isPrimaryBtnLoading={isUpdating}
      secondaryBtnText='Cancel'
      secondaryBtnAction={onClose}
      isPrimaryBtnDisabled={!isEditIssueFormValid}
    >
      <Box sx={{ p: 2, minWidth: 350 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Details
          </Typography>
          <Grid
            container
            direction='column'
            spacing={1}
            sx={(theme) => ({ background: theme.palette.background.main, borderRadius: 1, p: 2 })}
          >
            <Grid>
              <Typography variant='bodySmall'>
                <b>Created:</b>{' '}
                {selectedIssue.createdAt
                  ? format(new Date(selectedIssue.createdAt), getDateFormatter(clockFormat, LongDateTimeFormat))
                  : '-'}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant='bodySmall'>
                <b>Description:</b> {selectedIssue.description || '-'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Actions
          </Typography>
          <Form formId='edit-uw-issue-form' methods={editIssueForm}>
            <FormSection fields={editIssueFields} />
          </Form>
        </Box>
        {updateTaskError && (
          <Box color='error.main' mt={2}>
            {updateTaskError}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
